import useSWRFetcher from "./fetcher";
import settings from "../../../constants/settings";
import useSWRImmutable from "swr/immutable";
import { dateToLocalDateString } from "../../valueFormatting";
import { LeadTimeOptions } from "../../../reducers/uploadedBoard";

export function isInThePast(date: Date) {
  const today = new Date();
  return dateToLocalDateString(date) < dateToLocalDateString(today);
}

export function isToday(date: Date) {
  const today = new Date();
  return dateToLocalDateString(date) === dateToLocalDateString(today);
}

export function isWeekend(date: Date) {
  const dayOfWeek = date.getDay();
  return dayOfWeek == 0 || dayOfWeek == 6;
}

export function isClosedOnDate(date: Date, leadTimeOptions: LeadTimeOptions) {
  const closure = leadTimeOptions.dates_closed.find(
    (closure) => closure.date == dateToLocalDateString(date),
  );
  if (closure !== undefined) {
    return closure.description;
  }
  return null;
}

export function isBeforeFirstShipDate(
  date: Date,
  leadTimeOptions: LeadTimeOptions,
) {
  return leadTimeOptions.options
    .filter((option) => !option.disabled)
    .every((option) => {
      return dateToLocalDateString(date) < (option.ship_date as string);
    });
}

export const FASTEST_BASE_LEAD_TIME = 3;
export const FASTER_BASE_LEAD_TIME = 5;
export const STANDARD_BASE_LEAD_TIME = 10;

export function getOption(leadTimeOptions: LeadTimeOptions, baseDays: number) {
  return leadTimeOptions.options.find(
    (option) => option.base_days === baseDays && !option.disabled,
  );
}

function isOption(
  date: Date,
  leadTimeOptions: LeadTimeOptions,
  baseDaysInQuestion: number,
  nextBaseDays?: number,
) {
  if (isWeekend(date)) {
    return false;
  }
  const optionInQuestion = getOption(leadTimeOptions, baseDaysInQuestion);
  if (optionInQuestion === undefined) {
    return false;
  }
  const nextOption =
    nextBaseDays !== undefined
      ? getOption(leadTimeOptions, nextBaseDays)
      : undefined;
  const dateString = dateToLocalDateString(date);
  if (nextOption === undefined) {
    return (optionInQuestion.ship_date as string) <= dateString;
  }
  return (
    (optionInQuestion.ship_date as string) <= dateString &&
    dateString < (nextOption.ship_date as string)
  );
}

export function isFastestOption(date: Date, leadTimeOptions: LeadTimeOptions) {
  return isOption(
    date,
    leadTimeOptions,
    FASTEST_BASE_LEAD_TIME,
    FASTER_BASE_LEAD_TIME,
  );
}

export function isFasterOption(date: Date, leadTimeOptions: LeadTimeOptions) {
  return isOption(
    date,
    leadTimeOptions,
    FASTER_BASE_LEAD_TIME,
    STANDARD_BASE_LEAD_TIME,
  );
}

export function isStandardOption(date: Date, leadTimeOptions: LeadTimeOptions) {
  return isOption(date, leadTimeOptions, STANDARD_BASE_LEAD_TIME);
}

// If you're using this in the context of a particular board, use the lead_time_options field
// on the quote instead, which will have options relevant to that particular board.
// This is only for showing generic options and office closures.
export function useExampleLeadTimeOptions() {
  const fetcher = useSWRFetcher<LeadTimeOptions>();

  const { data } = useSWRImmutable<LeadTimeOptions>(
    `${settings.api_url}/boards/lead-time-options`,
    fetcher,
  );

  return data || null;
}
